import React, {
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { IconBaseProps } from 'react-icons';
import { FiAlertCircle } from 'react-icons/fi';
import { useField } from '@unform/core';

import { Container, Error } from './styles';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  icon?: React.ComponentType<IconBaseProps>;
}

const InputWithoutTitleAndFormattedToLower: React.FC<InputProps> = ({
  name,
  icon: Icon,
  disabled,
  // Extraído do `rest` de propósito — ver `handleInputBlur`.
  onBlur,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isFilled, setIsFilled] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { fieldName, defaultValue, error, registerField } = useField(name);

  const handleInputFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  /**
   * ⚠️ **Compõe com o `onBlur` de quem usa o componente, em vez de ser substituído por
   * ele.** O `onBlur` interno vinha **antes** do `{...rest}`, então qualquer chamador que
   * passasse `onBlur` o sobrescrevia em silêncio e levava embora as três coisas daqui: o
   * estado de foco (a borda ficava acesa para sempre), o `isFilled` e — o pior — a
   * **normalização para minúsculas**, de que a confirmação de e-mail da 0.30 depende para
   * não acusar divergência por diferença de caixa. Achado na 0.31, ao ligar a checagem de
   * duplicidade no `blur` do cadastro.
   *
   * A ordem importa: o callback externo roda **depois** da normalização, então quem lê o
   * campo no `blur` lê o valor já normalizado.
   */
  const handleInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);

      setIsFilled(!!inputRef.current?.value);

      if (inputRef.current) {
        inputRef.current.value = inputRef.current.value.trim().toLowerCase();
      }

      onBlur?.(event);
    },
    [onBlur],
  );

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [fieldName, registerField]);
  return (
    <Container
      isErrored={!!error}
      isFilled={isFilled}
      isFocused={isFocused}
      isDisabled={!!disabled}
    >
      {Icon && <Icon size={20} />}
      <input
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        defaultValue={defaultValue}
        ref={inputRef}
        disabled={disabled}
        {...rest}
      />
      {error && (
        <Error title={error}>
          <FiAlertCircle color="#c53030" size={20} />
        </Error>
      )}
    </Container>
  );
};

export default InputWithoutTitleAndFormattedToLower;
